import type { StoredProfile } from "../types";

const PROFILE_KEY = "serendiptv.profile.v1";
const MAX_VOLUME = 1;

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  // Keep the fallback UUID-shaped because the shared API contract validates
  // anonymous identifiers as UUIDs even when Web Crypto is unavailable.
  const random = () => Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, "0");
  return `${random()}-${random().slice(0, 4)}-4${random().slice(0, 3)}-${(8 + Math.floor(Math.random() * 4)).toString(16)}${random().slice(0, 3)}-${random()}${random().slice(0, 4)}`;
}

const DEFAULT_PROFILE: StoredProfile = {
  viewerId: "",
  volume: 0.42,
  muted: false,
  audioUnlocked: false,
  firstSessionHintSeen: false
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function normalizeProfile(raw: unknown): StoredProfile {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_PROFILE, viewerId: newId() };

  const candidate = raw as Partial<StoredProfile>;
  const volume = typeof candidate.volume === "number" && Number.isFinite(candidate.volume)
    ? Math.min(MAX_VOLUME, Math.max(0, candidate.volume))
    : DEFAULT_PROFILE.volume;

  const lastChannel = typeof candidate.lastChannel === "number" && Number.isInteger(candidate.lastChannel)
    ? Math.min(69, Math.max(2, candidate.lastChannel))
    : undefined;

  return {
    viewerId: typeof candidate.viewerId === "string" && UUID_PATTERN.test(candidate.viewerId) ? candidate.viewerId : newId(),
    volume,
    muted: candidate.muted === true,
    audioUnlocked: candidate.audioUnlocked === true,
    lastChannel,
    firstSessionHintSeen: candidate.firstSessionHintSeen === true
  };
}

/** Storage is optional. A privacy setting or Safari private window must not strand the TV. */
export function loadProfile(): { profile: StoredProfile; available: boolean } {
  if (typeof window === "undefined") return { profile: { ...DEFAULT_PROFILE, viewerId: newId() }, available: false };

  try {
    const raw = window.localStorage.getItem(PROFILE_KEY);
    const profile = normalizeProfile(raw ? JSON.parse(raw) : null);
    // A read may succeed while writes are blocked (notably in some private
    // browsing/privacy configurations). Treat that as unavailable so callers
    // accurately report the degraded state but can still keep watching.
    if (!raw) return { profile, available: persistProfile(profile) };
    return { profile, available: true };
  } catch {
    return { profile: { ...DEFAULT_PROFILE, viewerId: newId() }, available: false };
  }
}

export function persistProfile(profile: StoredProfile): boolean {
  if (typeof window === "undefined") return false;

  try {
    window.localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    return true;
  } catch {
    return false;
  }
}

export function updateProfile(patch: Partial<StoredProfile>): StoredProfile {
  const loaded = loadProfile();
  const next = normalizeProfile({ ...loaded.profile, ...patch });
  persistProfile(next);
  return next;
}

export function makeSessionId(): string {
  return newId();
}

export const storageKeys = { PROFILE_KEY };
